import Vue from 'vue';
import Vuex from 'vuex';

import auth from './modules/auth'
import employees from './modules/employees';
import checkIns from './modules/checkIns';
import performanceReviews from './modules/performanceReviews';
import categories from './modules/categories';
import departments from './modules/departments';
import positionTemplates from './modules/positionTemplates';
import defaultDiscussionPoints from './modules/defaultDiscussionPoints';
import coreValues from './modules/coreValues';
import hrDocuments from './modules/hrDocuments';
import marketplace from './modules/marketplace';
import meetingLocations from './modules/meetingLocations';
import performanceReviewTemplates from './modules/performanceReviewTemplates';
import reports from './modules/reports';
import tags from './modules/tags';
import features from './modules/features';
import productPlans from './modules/productPlans';
import assets from './modules/assets';
import assetConfiguration from './modules/assetConfiguration';
import ProductPlan from '@/enums/productPlan';
import ProductModule from '@/enums/productModule';
import onboardingModule from './modules/onboarding'
import privileges from './modules/privileges'
import certifications from './modules/certifications'
import certificationTypes from './modules/certificationTypes'
import certificationActions from './modules/certificationActions'
import forms from './modules/forms'
import { VuexBuilder } from '@helpers/vuexBuilder';

Vue.use(Vuex);

export default new Vuex.Store({
	modules: {
		auth,
		employees,
		performanceReviews,
		checkIns,
		categories,
		departments,
		positionTemplates,
		defaultDiscussionPoints,
		coreValues,
		hrDocuments,
		marketplace,
		meetingLocations,
		performanceReviewTemplates,
		reports,
		tags,
		features,
		assets,
		assetConfiguration,
		productPlans,
		onboardingModule,
		privileges,
		certifications,
		certificationTypes,
		certificationActions,
		forms
	},
	state: {
		pageTitle: 'Your HR System',
		tenantId: null,
		isAuthenticated: false,
		user: null,
		drawer: null,
		myToDoList: [],
		bootstrapData: {
			user_isLoading: false,
			user_isLoaded: false,
			employees_isLoading: false,
			employees_isLoaded: false,
			performanceReviews_isLoading: false,
			performanceReviews_isLoaded: false,
			categories_isLoading: false,
			categories_isLoaded: false,
			departments_isLoading: false,
			departments_isLoaded: false,
			positionTemplates_isLoading: false,
			positionTemplates_isLoaded: false,
			defaultDiscussionPoints_isLoading: false,
			defaultDiscussionPoints_isLoaded: false,
			coreValues_isLoading: false,
			coreValues_isLoaded: false,
			hrDocuments_isLoading: false,
			hrDocuments_isLoaded: false,
			hrDocumentTemplates_isLoading: false,
			hrDocumentTemplates_isLoaded: false,
			meetingLocations_isLoading: false,
			meetingLocations_isLoaded: false,
			performanceReviewTemplates_isLoading: false,
			performanceReviewTemplates_isLoaded: false,
			checkIns_isLoading: false,
			checkIns_isLoaded: false,
			tagList_isLoading: false,
			tagList_isLoaded: false,
			bootstrapData_isLoading: false,
			bootstrapData_isLoaded: false
		},
		loggedInEmployee: null
	},
	getters: {
		loggedInUser: state => {
			return state.user;
		},
		loggedInEmployee: state => {
			return state.loggedInEmployee;
		},
		loggedInTenant: state => {
			return state.user != null ? state.user.tenants.find(x => x.id === state.tenantId) : null;
		},
		loggedInSubscription: (state, getters) => {
			if (!state.user || !getters.loggedInTenant) return null
			return getters.loggedInTenant.subscription
		},
		availableTenants: state => {
			return state.user != null ? state.user.tenants.filter(x => x.status === 'Active') : [];
		},
		isBootstrapped: (state, getters) => {
			return (
				state.bootstrapData.user_isLoaded &&
				state.bootstrapData.employees_isLoaded &&
				(getters.hasAccessToModule('positionDescriptions') ? state.bootstrapData.positionTemplates_isLoaded : true) &&
				(getters.hasAccessToModule('performanceReviews') ? state.bootstrapData.defaultDiscussionPoints_isLoaded : true) &&
				(getters.hasAccessToModule('compliance') ? state.bootstrapData.hrDocuments_isLoaded : true) &&
				state.bootstrapData.bootstrapData_isLoaded &&
				state.productPlans.productPlans_loaded
			);
		},
		myMessages: (state, getters, rootState, rootGetters) => {
			let messages = [];
			const hrDocumentUpdates = rootGetters['hrDocuments/hrDocumentsWithUpdatesAvailable'];
			if (hrDocumentUpdates.length > 0) {
				messages = [...messages, ...hrDocumentUpdates.map(x => {
					return {
						type: 'hrDocumentUpdate',
						id: x.id,
						message: 'Policy update available',
						title: `<strong>${x.name}</strong> has a new version available for use`
					}
				})];
			}
			return messages;
		},
		myToDoList(state) {
			return state.myToDoList.items;
		},
		hasUnderlingsOrAdmin(state, getters) {
			return getters.loggedInEmployee != null && (getters.loggedInEmployee.isAdmin || (getters.loggedInEmployee.underlings != null && getters.loggedInEmployee.underlings.length > 0));
		},
		productPlan: (state, getters) => {
			if (getters.loggedInTenant == null) return null;
			if (getters.loggedInTenant.productPlan == null) return ProductPlan.Full;

			return getters.loggedInTenant.productPlan;
		},
		hasAccessToModule: (state, getters, rootState, rootGetters) => (module) => {
			// New way of checking access (based on subscription modules)
			const subscription = rootGetters['productPlans/productPlanCurrentlySubscribedTo'];

			if (subscription && subscription.modules) {
				return subscription.modules.includes(module);
			}

			// Before we integrated Stripe there was two 'ProductPlans' which were billed manually
			// We check this old way whilst existing clients are transitioning over to the new subscription method
			const productPlanLegacy = getters.productPlan;

			if (productPlanLegacy === ProductPlan.Full) return true;

			if (productPlanLegacy === ProductPlan.Compliance)
				return [ProductModule.PositionDescriptions, ProductModule.Compliance, ProductModule.OrgChart].includes(module);

			return false;
		},
		loggedInEmployeeIsAdminOrManager: state => state.loggedInEmployee && (state.loggedInEmployee.underlings.length || state.loggedInEmployee.roles.some(role => role === 'admin')),
		loggedInEmployeeIsAdmin: state => state.loggedInEmployee && state.loggedInEmployee.roles.some(role => role === 'admin'),
	},
	actions: {
		setBootstrapDataPropIsLoading({ commit }, prop) {
			commit('setBootstrapDataPropIsLoading', prop);
		},
		setBootstrapDataPropIsLoaded({ commit }, prop) {
			commit('setBootstrapDataPropIsLoaded', prop);
		},
		setPageTitle({ commit }, data) {
			commit('setPageTitle', data);
		},
		setTenantId({ commit }, data) {
			return new Promise((resolve, reject) => {
				commit('setTenantId', data);
				resolve();
			});
		},
		setIsAuthenticated({ commit }, data) {
			return new Promise((resolve, reject) => {
				commit('setIsAuthenticated', data);
				resolve();
			});
		},
		getBootstapApplicationData({ commit, rootState }) {
			return new Promise((resolve, reject) => {
				commit('BOOTSTRAPPING_APPLICATION');
				return Vue.axios
					.get(`/api/${rootState.tenantId}/bootstrapApplication`)
					.then(response => {
						commit('categories/GET_SUCCESS', response.data.categories, { root: true });
						commit('coreValues/GET_SUCCESS', response.data.coreValues, { root: true });
						commit('departments/GET_SUCCESS', response.data.departments, { root: true });
						commit('marketplace/GET_DOC_TEMPLATES_SUCCESS', response.data.hrDocumentTemplates, { root: true });
						commit('meetingLocations/GET_SUCCESS', response.data.meetingLocations, { root: true });
						commit('performanceReviewTemplates/GET_SUCCESS', response.data.performanceReviewTemplates, { root: true });
						commit('tags/GET_TAGLIST_SUCCESS', response.data.tagList, { root: true });
						commit('hrDocuments/GET_CATEGORY_LIST_SUCCESS', response.data.hrDocumentCategoryList, { root: true });

						commit('BOOTSTRAP_APPLICATION_SUCCESS');

						resolve(response.data);
					})
					.catch(error => {
						//commit('BOOTSTRAP_APPLICATION_FAILURE', error);
						reject(error);
					});
			});
		},
		getLoggedInUser({ commit }) {
			return new Promise((resolve, reject) => {
				commit('GET_USER_PENDING');
				return Vue.axios
					.get(`/api/users`)
					.then(response => {
						commit('GET_USER_SUCCESS', response.data);
						resolve(response.data);
					})
					.catch(error => {
						commit('GET_USER_FAILURE', error);
						reject(error);
					});
			});
		},
		getMyToDoList({ commit, rootState, getters }) {
			return new Promise((resolve, reject) => {
				commit('GET_TODO_LIST_PENDING');
				return Vue.axios
					.get(`/api/${rootState.tenantId}/employees/me/toDoList`)
					.then(response => {
						commit('GET_TODO_LIST_SUCCESS', response.data);
						resolve(response.data);
					})
					.catch(error => {
						commit('GET_TODO_LIST_FAILURE', error);
						reject(error);
					});
			});
		},
		sendAppFeedback({ rootState }, feedback) {
			return new Promise((resolve, reject) => {
				Vue.axios
					.post(`/api/${rootState.tenantId}/feedback`, feedback)
					.then(resp => {
						resolve(resp);
					})
					.catch(err => {
						console.log('Error sending feedback');
						console.log(err);
						reject(err);
					});
			});
		},
		initialiseAccessibleModules({ getters, dispatch }) {
			if (getters.hasAccessToModule(ProductModule.CheckIns)) {
				dispatch('setBootstrapDataPropIsLoading', 'checkIns');
				dispatch('checkIns/get')
					.then(() => dispatch('setBootstrapDataPropIsLoaded', 'checkIns'))
			}

			if (getters.hasAccessToModule(ProductModule.PerformanceReviews)) {
				dispatch('setBootstrapDataPropIsLoading', 'performanceReviews');
				dispatch('performanceReviews/get')
					.then(() => dispatch('setBootstrapDataPropIsLoaded', 'performanceReviews'))

				dispatch('setBootstrapDataPropIsLoading', 'defaultDiscussionPoints');
				dispatch('defaultDiscussionPoints/get')
					.then(() => dispatch('setBootstrapDataPropIsLoaded', 'defaultDiscussionPoints'));
			}

			if (getters.hasAccessToModule(ProductModule.PositionDescriptions)) {
				dispatch('setBootstrapDataPropIsLoading', 'positionTemplates');
				dispatch('positionTemplates/get')
					.then(() => dispatch('setBootstrapDataPropIsLoaded', 'positionTemplates'))
			}

			if (getters.hasAccessToModule(ProductModule.Compliance)) {
				dispatch('setBootstrapDataPropIsLoading', 'hrDocuments');
				dispatch('hrDocuments/get')
					.then(() => dispatch('setBootstrapDataPropIsLoaded', 'hrDocuments'))
			}

			dispatch('certifications/initialize')
		},
		async uploadLogo({commit, rootState}, file) {
			const formData = new FormData();
			formData.append('logo', file);

			return await new VuexBuilder(commit)
				.request(() => Vue.axios.post(`/api/${rootState.tenantId}/TenantOnboarding/logo`, formData, {
					headers: { 'Content-Type': 'multipart/form-data' }
				}))
				.successToast('Logo uploaded successfully')
				.errorToast('Failed to upload logo')
				.commitData('LOGO_UPLOAD_SUCCESS')
				.execute()
		}
	},
	mutations: {
		setBootstrapDataPropIsLoading(state, prop) {
			state.bootstrapData[`${prop}_isLoading`] = true;
		},
		setBootstrapDataPropIsLoaded(state, prop) {
			state.bootstrapData[`${prop}_isLoaded`] = true;
			state.bootstrapData[`${prop}_isLoading`] = false;
		},
		setPageTitle(state, data) {
			state.pageTitle = data;
		},
		setTenantId(state, data) {
			state.tenantId = data;
		},
		setIsAuthenticated(state, data) {
			state.isAuthenticated = data;
		},
		GET_USER_PENDING(state) {
			state.bootstrapData.user_isLoading = true;
		},
		GET_USER_SUCCESS(state, data) {
			state.bootstrapData.user_isLoading = false;
			state.bootstrapData.user_isLoaded = true;
			state.user = data;
		},
		GET_USER_FAILURE(state, error) {
			state.bootstrapData.user_isLoading = false;
			console.log(error);
		},
		GET_TODO_LIST_PENDING(state) {

		},
		GET_TODO_LIST_SUCCESS(state, data) {
			state.myToDoList = data;
		},
		GET_TODO_LIST_FAILURE(state, error) {
			
		},
		BOOTSTRAPPING_APPLICATION(state) {
			state.bootstrapData.bootstrapData_isLoading = true;
		},
		BOOTSTRAP_APPLICATION_SUCCESS(state) {
			state.bootstrapData.bootstrapData_isLoading = false;
			state.bootstrapData.bootstrapData_isLoaded = true;	
		},
		SET_LOGGED_IN_EMPLOYEE(state, loggedInEmployee) {
			state.loggedInEmployee = loggedInEmployee;
		},
		SET_LOGGED_IN_EMPLOYEE_SUBSCRIPTION(state, subscription) {
			state.user.tenants.find(x => x.id === state.tenantId).subscription = subscription;
		},
		UPDATE_PERFORMANCE_REVIEW_SETTINGS(state, settings) {
			if (!state.user) return;

			const tenant = state.user.tenants.find(x => x.id === state.tenantId);
			if (!tenant) return;

			if (!tenant.tenantSettings) {
				Vue.set(tenant, 'tenantSettings', {});
			}

			Vue.set(
				tenant.tenantSettings,
				'allowReviewerCompletionWithoutEmployeeSubmission',
				!!settings.allowReviewerCompletionWithoutEmployeeSubmission
			);
		},
		UPDATE_POSITION_DESCRIPTION_SETTINGS(state, { tenantId, settings }) {
			if (!state.user) return;

			const tenant = state.user.tenants.find(x => x.id === tenantId);
			if (!tenant) return;

			if (!tenant.tenantSettings) {
				Vue.set(tenant, 'tenantSettings', {});
			}

			Vue.set(
				tenant.tenantSettings,
				'requireEmployeeAcknowledgementForPositionDescriptions',
				settings.requireEmployeeAcknowledgementForPositionDescriptions !== false
			);
		},
		LOGO_UPLOAD_SUCCESS(state, logoUrl) {
			state.user.tenants.find(x => x.id === state.tenantId).logoUrl = logoUrl
		}
	},
});
